import { useMemo } from "react";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useLang } from "../i18n/LangContext";
import { useAppState } from "../state/AppState";
import { formatPrice } from "../state/fxRates";
import { headlineCardPrice, lowestDayPrice } from "../state/pricing";
import { spotSourceKey, todayIso } from "../state/filters";

export function SpotList() {
  const { t, speciesLabel } = useLang();
  const {
    visibleSpots,
    selectedId,
    selectSpot,
    currency,
    rates,
    dispatchFilter,
    filters,
  } = useAppState();

  const sorted = useMemo(
    () => [...visibleSpots].sort((a, b) => a.name.localeCompare(b.name, "de")),
    [visibleSpots],
  );

  const fpShort = t("fpMembershipShort");
  const cardShort = t("cardYearShort");
  // The date filter defaults to today, so it only counts as "active"
  // when the user has actually picked a different day. Without this
  // guard the reset button would always be visible.
  const today = todayIso();
  const filtersActive =
    !!filters.q ||
    !!filters.species ||
    !!filters.method ||
    filters.onlyInSeason ||
    (!!filters.date && filters.date !== today) ||
    filters.enabledSources.size < 6;

  return (
    <section className="results">
      <header className="results__header">
        <h2>{`${sorted.length} ${t("spotsLabel")}`}</h2>
        {filtersActive && (
          <Button
            onClick={() => dispatchFilter({ type: "reset" })}
            startIcon={<RestartAltIcon sx={{ fontSize: 14 }} />}
            sx={{
              color: "text.secondary",
              bgcolor: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 999,
              px: 1.25,
              py: 0.25,
              minHeight: 0,
              fontSize: 12,
              fontWeight: 500,
              textTransform: "none",
              letterSpacing: 0,
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.06)",
                borderColor: "rgba(255,255,255,0.12)",
                color: "text.primary",
              },
              "& .MuiButton-startIcon": { mr: 0.5, ml: -0.25 },
            }}
          >
            {t("reset")}
          </Button>
        )}
      </header>
      <ul id="spot-list">
        {sorted.length === 0 && <li className="results__empty">{t("noMatches")}</li>}
        {sorted.map((spot) => {
          const lp = !spot.isFree ? lowestDayPrice(spot) : null;
          const card = !spot.isFree && !lp ? headlineCardPrice(spot, fpShort, cardShort) : null;
          const priceLabel = spot.isFree
            ? t("freeBadge")
            : lp
              ? formatPrice(lp.amount, currency, rates)
              : card
                ? `${formatPrice(card.amount, currency, rates)} ${t("perYear")}`
                : "—";
          const speciesShort = spot.species
            .slice(0, 3)
            .map((s) => speciesLabel(s))
            .join(" · ");
          const srcKey = spotSourceKey(spot);
          // Match the detail page exactly: full source name (e.g. "svfr.is")
          // and the same .chip--src-* color class so the pills look identical
          // in both places.
          const srcLabel = spot.isFree ? t("freeBadge") : spot.source.name;
          const srcCssKey = spot.isFree ? "free" : srcKey.replace(".is", "");
          return (
            <li
              key={spot.id}
              className={spot.id === selectedId ? "active" : undefined}
              onClick={() => selectSpot(spot.id)}
            >
              <div className="spot-row__title">
                <span className="spot-row__name">{spot.name}</span>
                <span className="spot-row__price">{priceLabel}</span>
              </div>
              <div className="spot-row__meta">
                {speciesShort && <span>{speciesShort}</span>}
                <Chip
                  size="small"
                  label={srcLabel}
                  className={`chip--src-${srcCssKey}`}
                  variant="outlined"
                  sx={{ ml: "auto" }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
